import { http } from '@/lib/ats-https';
import { useMutation, useQuery } from '@tanstack/react-query';

// Shapes mirror ~/Projects/ENG-3893/contract.yml (seam_1_list_attachable,
// seam_2_persist_selection) exactly — core-web is the CONSUMER. Do not add a
// field here that the contract doesn't carry; drift gets reported, not patched.

export type AttachableRefType =
  | 'offer_document'
  | 'scorecards'
  | 'resume'
  | 'application_file';

// No 'offer_documents': they attach to the approval email unconditionally, so
// core stops listing them as a picker group (contract.yml amendment_13).
export type AttachableGroupKey = 'candidate_details' | 'other_files';

// Only the synthetic `scorecards` item ever carries this; every other ref_type is
// implicitly 'ready'.
//   ready       blob exists — normal row, real byte_size and url
//   pending     scorecards exist, nothing rendered yet. ENABLED and tickable — this
//               is the state the recruiter ticks to start the render on Add
//   generating  render in flight — disabled, spinner, byte_size 0, url ''
//   unavailable no scorecards at all — disabled, and never resolves on its own
// Only 'generating' is a loading state; treating 'pending' or 'unavailable' as one
// would poll forever and gate Send permanently (see hasGeneratingScorecard).
export type ScorecardStatus =
  | 'ready'
  | 'pending'
  | 'generating'
  | 'unavailable';

export interface AttachableItem {
  ref_type: AttachableRefType;
  ref_id: string | null;
  filename: string;
  // contract.yml seam_1 + amendment_6_pregenerate_scorecard_pdf — both are
  // required on every item, every group, always (never optional/null). A
  // resume/scorecards row with nothing behind it is `disabled: true` instead
  // of omitting these — see the resume/scorecards fallbacks in
  // AttachableDocumentsBuilder, which always populate a real content_type
  // string and a real (possibly 0) byte_size, never undefined.
  content_type: string;
  byte_size: number;
  selected: boolean;
  // contract.yml amendment_3c_disabled_is_universal — present on EVERY item in
  // every group, not only candidate_details (always false for other_files,
  // whose items are only ever items backed by a real blob).
  disabled: boolean;
  // contract.yml AMENDMENT 1 (amendment_1_preview_urls) — always present,
  // never null. amendment_6_pregenerate_scorecard_pdf later made `scorecards`
  // a normal pre-generated blob too (the render-on-demand endpoint that used
  // to back it is deleted) — every ref_type now resolves to a plain signed
  // blob URL. The client treats this uniformly regardless — never
  // special-cased ref_type even when scorecards WAS the odd one out.
  url: string;
  // AMENDMENT 9 — present only on the `scorecards` item; undefined on every
  // other ref_type, which is always implicitly "ready" (never rendered
  // client-side as a distinct state).
  status?: ScorecardStatus;
}

// contract.yml AMENDMENT 2 (amendment_2_resolved_attachments) — the offer show
// payload's RESOLVED offer-approval selections, for the approver drawer's
// Documents list. Same five keys as seam_3's additional_document_files, so one
// renderer serves both the email and the drawer. Distinct from the
// pre-existing `attachments` field on the offer — never overload that one.
//
// byte_size was briefly `number | null` (amendment_3a: scorecards rendered
// lazily, so it could be "not yet rendered"). amendment_6_pregenerate_scorecard_pdf
// superseded that — the scorecard PDF is generated ahead of time, so this is
// always a real integer now, for every ref_type, with no nil case.
//
// amendment_4_no_duplicate_offer_documents: core omits any ref_type:
// "offer_document" entry here when that same file is already exposed via
// getOfferDocumentFiles/offer_documents_pdf — this array means "resolved
// selections the drawer would not otherwise show," not every selection.
export interface ResolvedApprovalAttachment {
  ref_type: string;
  filename: string;
  content_type: string;
  byte_size: number;
  url: string;
}

export interface AttachableGroup {
  key: AttachableGroupKey;
  label: string;
  disabled: boolean;
  items: AttachableItem[];
}

export interface AttachableDocumentsResponse {
  groups: AttachableGroup[];
}

export interface SelectionRef {
  ref_type: AttachableRefType;
  ref_id: string | null;
}

export interface PersistApprovalAttachmentsResponse {
  count: number;
}

export const attachFilesAPI = {
  // GET /api/ats/v1/ats_offers/:id/attachable_documents (seam_1)
  getAttachableDocuments: async (
    offerId: string | number
  ): Promise<AttachableDocumentsResponse> => {
    const response = await http.get(
      `/ats_offers/${offerId}/attachable_documents`
    );
    return response.data;
  },

  // PUT /api/ats/v1/ats_offers/:id/approval_attachments (seam_2)
  // Selection is a set of REFERENCES (not blobs) — never call delete_attachment
  // from this path; that endpoint purges the underlying file (contract MUST_NOT).
  persistApprovalAttachments: async ({
    offerId,
    selections,
  }: {
    offerId: string | number;
    selections: SelectionRef[];
  }): Promise<PersistApprovalAttachmentsResponse> => {
    const response = await http.put(
      `/ats_offers/${offerId}/approval_attachments`,
      { selections }
    );
    return response.data;
  },
};

export const attachFilesKeys = {
  all: ['offer-details', 'attachable-documents'],
  byOffer: (offerId?: string | number) => [...attachFilesKeys.all, offerId],
};

// AMENDMENT 9 — true while ANY item (in practice only ever the synthetic
// scorecards row) is mid-render. `unavailable` deliberately does NOT count:
// it is a permanent disabled state, not one that will ever resolve to
// 'ready' on its own, so treating it as "generating" would poll forever.
export const hasGeneratingScorecard = (
  data?: AttachableDocumentsResponse
): boolean =>
  (data?.groups ?? []).some((group) =>
    group.items.some((item) => item.status === 'generating')
  );

// Gotenberg renders in ~3-15s (contract.yml amendment_9). 4s keeps the
// picker's loader and the footer's Send gate feeling responsive without
// hammering the endpoint — one or two refetches for the common case, at
// most ~4 for the slow tail.
export const SCORECARD_GENERATION_POLL_INTERVAL_MS = 4000;

export const useGetAttachableDocumentsQuery = (
  offerId?: string | number,
  options: Record<string, unknown> = {}
) => {
  return useQuery({
    queryKey: attachFilesKeys.byOffer(offerId),
    queryFn: () => attachFilesAPI.getAttachableDocuments(offerId as string),
    staleTime: 0,
    enabled: !!offerId,
    // AMENDMENT 9 consumer_rule — poll while the scorecards row is
    // 'generating'; stop the instant it isn't (covers 'ready' AND
    // 'unavailable' alike, neither of which needs another fetch).
    // `query.state.data` is the latest cached response, so this
    // re-evaluates after every refetch with no manual reopen needed.
    refetchInterval: (query: {
      state: { data?: AttachableDocumentsResponse };
    }) =>
      hasGeneratingScorecard(query.state.data)
        ? SCORECARD_GENERATION_POLL_INTERVAL_MS
        : false,
    ...options,
  });
};

// A stable key so anything outside this component tree can ask "is a selection
// still being saved?" through `useIsMutating`, rather than the state being
// mirrored into the offer store and going stale on unmount. The footer's "Send
// offer for approval" button and the placeholder chip both read it.
export const PERSIST_APPROVAL_ATTACHMENTS_MUTATION_KEY = [
  'approval-attachments',
  'persist',
] as const;

export const usePersistApprovalAttachmentsMutation = (
  options: Record<string, unknown> = {}
) => {
  return useMutation({
    mutationKey: PERSIST_APPROVAL_ATTACHMENTS_MUTATION_KEY,
    mutationFn: attachFilesAPI.persistApprovalAttachments,
    ...options,
  });
};
